import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { AdvancedApiError } from './client'

export type AdvancedResourceState<T> =
  | { status: 'loading' }
  | { status: 'ready'; data: T }
  | { status: 'error'; error: unknown }

// Access is gone, so stale data must not outlive it (ADR-016).
const revokesAccess = (error: unknown) =>
  error instanceof AdvancedApiError && (error.status === 401 || error.status === 404)

export const useAdvancedResource = <T>(
  fetcher: (signal: AbortSignal) => Promise<T>,
  deps: unknown[],
  skip = false
): AdvancedResourceState<T> & { retry: () => void } => {
  const [attempt, setAttempt] = useState(0)
  const [state, setState] = useState<AdvancedResourceState<T>>({ status: 'loading' })
  const retry = useCallback(() => setAttempt((current) => current + 1), [])
  const lastAttempt = useRef(attempt)

  useEffect(() => {
    if (skip) {
      return undefined
    }

    const refreshing = attempt !== lastAttempt.current
    lastAttempt.current = attempt
    const controller = new AbortController()
    // A retry refreshes in place: dropping ready data to `loading` unmounts whatever renders it.
    setState((current) => (refreshing && current.status === 'ready' ? current : { status: 'loading' }))

    // A fetch aborted after it resolved still runs its `then`.
    fetcher(controller.signal)
      .then((data) => {
        if (!controller.signal.aborted) {
          setState({ status: 'ready', data })
        }
      })
      .catch((error: unknown) => {
        if (!controller.signal.aborted) {
          setState((current) =>
            refreshing && current.status === 'ready' && !revokesAccess(error) ? current : { status: 'error', error }
          )
        }
      })

    return () => controller.abort()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...deps, skip, attempt])

  // Stable per state, since callers key effects on it.
  return useMemo(() => ({ ...state, retry }), [state, retry])
}
